import React, { useEffect, useMemo, useState } from 'react'
import { Badge, Button, Card, Col, Container, Form, Row } from 'react-bootstrap'
import { Bell, Calendar2Event, People } from 'react-bootstrap-icons'
import { LuGraduationCap } from 'react-icons/lu'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

export default function NotificationForm() {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo, name, id } = useInstitute();

  const [form, setForm] = useState({
    title: '',
    message: '',
    audience: 'All',
    course: '',
    className: '',
    scheduleAt: '',
  })

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const courses = useMemo(() => ['BCA', 'BBA', 'MCA', 'MBA'], [])
  const classes = useMemo(() => ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4'], [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Please fill Title and Message')
      return
    }
    toast.success('Notification saved (demo)')
    setForm((prev) => ({
      ...prev,
      title: '',
      message: '',
      course: '',
      className: '',
      scheduleAt: '',
    }))
  }

  const showCourseClass = form.audience === 'Student' || form.audience === 'All'

  return (
    <>
      <style>
        {`
          .page-title {
            font-family: 'Inter', system-ui, sans-serif;
            letter-spacing: -0.02em;
            color: #223843;
          }
          .modern-card {
            background: #ffffff;
            border: 1px solid #D1D3D4;
            border-radius: 12px;
            overflow: hidden;
          }
          .form-control, .form-select {
            border-radius: 10px !important;
            border: 1px solid #D1D3D4 !important;
            padding: 10px 12px !important;
            font-family: 'Inter', system-ui, sans-serif;
          }
          .form-label {
            font-weight: 600;
            color: #111827;
            font-family: 'Inter', system-ui, sans-serif;
          }
        `}
      </style>

      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: '12px',
          paddingRight: '12px',
        }}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
                gap: '12px',
              }}
            >
              <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3
                    className="m-0 fw-bold page-title"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: isMobile ? '14px' : 'clamp(18px, 2.5vw, 22px)',
                      lineHeight: '1.2',
                    }}
                  >
                    Create Notification
                  </h3>
                  <p className="m-0 mt-0 text-start" style={{ fontSize: isMobile ? '10px' : '13px', color: '#6b7280' }}>
                    Fill details and submit.
                  </p>
                </div>
              </div>

              {!isMobile && <ProfileDropdown logout={logout} notificationCount={3} showNotifications={true} />}
            </div>
          </div>
        </div>

        <Container
          className="mt-4"
          style={{
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
            maxWidth: '1200px',
          }}
        >
          <Form onSubmit={handleSubmit}>
            <Row className="g-4">
              {/* Notification Content */}
              <Col xs={12} md={6}>
                <Card
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      background:
                        'linear-gradient(135deg, #223843 0%, #223843 100%)',
                      padding: '16px 24px',
                      borderBottom: '1px solid #D1D3D4',
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '10px',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(10px)',
                          }}
                        >
                          <Bell size={18} style={{ color: '#ffffff' }} />
                        </div>
                        <h5
                          style={{
                            margin: 0,
                            fontWeight: '700',
                            fontSize: '16px',
                            color: '#ffffff',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Notification Content
                        </h5>
                      </div>
                      <Badge bg="light" text="primary" style={{ border: '1px solid rgba(255,255,255,0.4)' }}>
                        Demo
                      </Badge>
                    </div>
                  </div>

                  <Card.Body style={{ padding: '24px' }} className="text-start">
                    <Form.Group className="mb-3">
                      <Form.Label>Title *</Form.Label>
                      <Form.Control
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                        placeholder="Enter notification title"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-0">
                      <Form.Label>Message / Description *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        placeholder="Write your message..."
                        required
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              {/* Audience & Schedule */}
              <Col xs={12} md={6}>
                <Card
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      background:
                        'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                      padding: '16px 24px',
                      borderBottom: '1px solid #D1D3D4',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <People size={18} style={{ color: '#ffffff' }} />
                      </div>
                      <h5
                        style={{
                          margin: 0,
                          fontWeight: '700',
                          fontSize: '16px',
                          color: '#ffffff',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Audience & Schedule
                      </h5>
                    </div>
                  </div>

                  <Card.Body style={{ padding: '24px' }} className="text-start">
                    <Form.Group className="mb-3">
                      <Form.Label>Select Audience *</Form.Label>
                      <Form.Select
                        name="audience"
                        value={form.audience}
                        onChange={handleChange}
                        required
                      >
                        <option>Student</option>
                        <option>Teacher</option>
                        <option>All</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-0">
                      <Form.Label>
                        <span className="d-inline-flex align-items-center gap-2">
                          <Calendar2Event size={16} /> Date / Schedule (optional)
                        </span>
                      </Form.Label>
                      <Form.Control
                        type="datetime-local"
                        name="scheduleAt"
                        value={form.scheduleAt}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              {/* Optional Targeting */}
              {showCourseClass && (
                <Col xs={12}>
                  <Card
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '12px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        background:
                          'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                        padding: '16px 24px',
                        borderBottom: '1px solid #D1D3D4',
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '10px',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(10px)',
                          }}
                        >
                          <Bell size={18} style={{ color: '#ffffff' }} />
                        </div>
                        <h5
                          style={{
                            margin: 0,
                            fontWeight: '700',
                            fontSize: '16px',
                            color: '#ffffff',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Optional Targeting
                        </h5>
                      </div>
                    </div>

                    <Card.Body style={{ padding: '24px' }} className="text-start">
                      <Row className="g-3">
                        <Col xs={12} md={6}>
                          <Form.Group>
                            <Form.Label>Course (optional)</Form.Label>
                            <Form.Select
                              name="course"
                              value={form.course}
                              onChange={handleChange}
                            >
                              <option value="">Select course</option>
                              {courses.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                          <Form.Group>
                            <Form.Label>Class (optional)</Form.Label>
                            <Form.Select
                              name="className"
                              value={form.className}
                              onChange={handleChange}
                            >
                              <option value="">Select class</option>
                              {classes.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>

            {/* Action Bar (like AddNewStudent) */}
            <div
              className="d-flex justify-content-end gap-3 mt-4 pt-3"
              style={{ borderTop: '1px solid #E5E7EB' }}
            >
              <Button
                type="button"
                onClick={() => navigate('/Notifications')}
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: '#6b7280',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontWeight: '600',
                  fontSize: '15px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  minWidth: '140px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F3F4F6'
                  e.currentTarget.style.borderColor = '#9ca3af'
                  e.currentTarget.style.color = '#111827'
                  e.currentTarget.style.transform = 'translateY(-1px)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.color = '#6b7280'
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                style={{
                  backgroundColor: '#10B981',
                  border: 'none',
                  color: '#ffffff',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontWeight: '600',
                  fontSize: '15px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  minWidth: '160px',
                  boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#059669'
                  e.currentTarget.style.transform = 'translateY(-1px)'
                  e.currentTarget.style.boxShadow =
                    '0 4px 8px rgba(16, 185, 129, 0.3)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#10B981'
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow =
                    '0 2px 4px rgba(16, 185, 129, 0.2)'
                }}
              >
                Submit Notification
              </Button>
            </div>
          </Form>
        </Container>
      </div>
    </>
  )
}


